import * as PIXI from "pixi.js";
import { hexToNumber } from "../../utils/color";
import { calculatePathLine, LinePathAction } from "../../utils/line";
import type { LineProps } from "./types";
import type { LayoutValues } from "../layoutEngine/layout.types";

export type Line = ReturnType<typeof createLine>;
export type Point = {
  x: number;
  y: number;
};

type LineLayoutValues = LayoutValues & {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  intermediatePoints: Point[];
  markerEndSize?: number;
  turningRadius?: number;
  zIndex?: number;
};

type SetLineLayoutValues = Omit<
  LineLayoutValues,
  keyof LayoutValues | "zIndex"
> & {
  zIndex?: number;
};

const DEFAULT_MARKER_END_SIZE = 8;
export const createLine = (initialProps: LineProps) => {
  const pixiComponent: PIXI.Graphics = new PIXI.Graphics();

  let lastPathKey: string = "";
  let wasVisible: boolean = true;
  let props: LineProps = structuredClone(defaultProps);

  let isCulled = false;
  let layoutValues: LineLayoutValues = {
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
    intermediatePoints: [],
    zIndex: 0,
    x: 0,
    y: 0,
    width: 0,
    height: 0
  };

  updateProps(initialProps);

  return {
    get props() {
      return props;
    },
    get pixiComponent() {
      return pixiComponent;
    },
    isCulled: () => isCulled,
    setCulled: (culled: boolean) => {
      if (culled !== isCulled) {
        isCulled = culled;
        return true;
      }
      return false;
    },
    getLayoutValues: () => Object.freeze(layoutValues),
    setLayoutValues: (newValues: SetLineLayoutValues) => {
      layoutValues = {
        ...layoutValues,
        ...newValues,
        ...getBoundaryBox(newValues)
      };
    },
    updateProps,
    destroy,
    render
  };

  function updateProps(newProps: Partial<LineProps>) {
    props = {
      ...props,
      ...newProps,
      style: { ...props.style, ...newProps.style }
    };
  }

  function destroy() {
    pixiComponent.destroy();
  }

  function renderGraphics() {
    const { color, width } = props.style;
    const {
      startX,
      startY,
      endX,
      endY,
      intermediatePoints,
      markerEndSize,
      turningRadius
    } = layoutValues;
    // line will be drawn at 0, 0 and then moved to the startX, startY
    // that is why we are passing relative value here
    const { path, direction } = calculatePathLine({
      sourceX: 0,
      targetX: endX - startX,
      sourceY: 0,
      targetY: endY - startY,
      intermediatePoints: intermediatePoints.map(point => ({
        x: point.x - startX,
        y: point.y - startY
      })),
      turningRadius
    });

    pixiComponent.clear();
    pixiComponent.lineStyle({
      width: width ?? 1,
      color: hexToNumber(color ?? "#ff00ff"),
      alpha: 1
    });

    path.forEach(segment => {
      const [action, points] = Object.entries(segment)[0] as [
        LinePathAction,
        any
      ];
      switch (action) {
        case LinePathAction.MOVE:
          pixiComponent.moveTo(points.x, points.y);
          break;
        case LinePathAction.LINE:
          pixiComponent.lineTo(points.x, points.y);
          break;
        case LinePathAction.ARC_TO:
          pixiComponent.arcTo(
            points.x1,
            points.y1,
            points.x2,
            points.y2,
            points.radius
          );
          break;
        case LinePathAction.BEZIER:
          pixiComponent.bezierCurveTo(
            points.controlX1,
            points.controlY1,
            points.controlX2,
            points.controlY2,
            points.x,
            points.y
          );
          break;
      }
    });

    drawMarkerEnd({
      pixiComponent,
      direction,
      hexColor: color,
      start: { x: startX, y: startY },
      end: { x: endX, y: endY },
      markerEndSize
    });

    pixiComponent.position.x = startX;
    pixiComponent.position.y = startY;
  }

  function render() {
    const { visible: isVisible } = props.style;
    const { startX, startY, endX, endY, intermediatePoints, zIndex } =
      layoutValues;
    const shouldBeVisible: boolean = !!isVisible && !isCulled;

    if (shouldBeVisible !== wasVisible) {
      pixiComponent.visible = shouldBeVisible;
      wasVisible = shouldBeVisible;
    }

    if (!shouldBeVisible) {
      return;
    }
    const pathKey = `${props.isSelected},${props.isHovered},${props.style.color},${props.style.width},${zIndex},${startX},${startY},${endX},${endY},${intermediatePoints.map(point => `${point.x},${point.y}`).join(",")}`;

    pixiComponent.zIndex = zIndex !== undefined ? zIndex : 0;

    // Only re-render graphics if the path has changed
    if (lastPathKey !== pathKey) {
      renderGraphics();
      lastPathKey = pathKey;
    }
  }
};

function drawMarkerEnd({
  pixiComponent,
  direction,
  hexColor = "#ff00ff",
  start,
  end,
  markerEndSize = DEFAULT_MARKER_END_SIZE
}: {
  pixiComponent: PIXI.Graphics;
  direction: number;
  hexColor?: string;
  start: { x: number; y: number };
  end: { x: number; y: number };
  markerEndSize?: number;
}) {
  const halfSize = markerEndSize / 2;

  // Calculate the points of the tip of the arrow based on the end position
  const tipX = end.x - start.x;
  const tipY = end.y - start.y;

  // Calculate rest of the two points of the triangle, pointing right/left depending on the direction
  const point1X = tipX - markerEndSize * direction;
  const point1Y = tipY - halfSize;
  const point2X = tipX - markerEndSize * direction;
  const point2Y = tipY + halfSize;

  // Draw the arrow
  pixiComponent.lineStyle(1, hexToNumber(hexColor), 1);
  pixiComponent.beginFill(hexToNumber(hexColor));
  pixiComponent.moveTo(tipX, tipY);
  pixiComponent.lineTo(point1X, point1Y);
  pixiComponent.lineTo(point2X, point2Y);
  pixiComponent.closePath();
  pixiComponent.endFill();
}

const defaultProps: LineProps = {
  key: "",
  startNodeId: "",
  startNodeHash: "",
  endNodeId: "",
  endNodeHash: "",
  flowLineType: "swimlane",
  isSelected: false,
  isHovered: false,
  style: {
    color: "#000000",
    width: 1,
    visible: true
  }
};

const getBoundaryBox = (newValues: SetLineLayoutValues): LayoutValues => {
  const { startX, startY, endX, endY, intermediatePoints } = newValues;
  let minX = Math.min(startX, endX);
  let minY = Math.min(startY, endY);
  let maxX = Math.max(startX, endX);
  let maxY = Math.max(startY, endY);

  intermediatePoints.forEach(point => {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  });
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
};
